import { Box, Stack, Typography } from '@mui/material';
import { usersCalendarEventsList } from 'djangogen/client';
import { useEffect, useMemo, useRef, useState } from 'react';
import useSwr from 'swr';

import { getRoomWeekColor } from '../../Utils/calendarColors';
import { dayjs } from '../Dayjs/dayjs-utils';

import type { MultiRoomTarget } from './InfoscreenMultiRoomCalendarView';
import type { MultiRoomPositionedEvent } from './roomCalendarUtils';
import {
    ALL_DAY_ROW_HEIGHT,
    clampMinutes,
    DAY_HEADER_HEIGHT,
    EVENT_LINE_HEIGHT,
    FALLBACK_GRID_BODY_HEIGHT_PX,
    formatCompactEventTimeRange,
    getHourLinePercentage,
    getHourRangeMinutes,
    getMultiRoomWeekDayData,
    getTimedEventLayout,
    getUniformEventTypography,
    getVisibleTimeLabel,
    getWeekHourRange,
    getWeekStart,
    isWeekendDay,
    TIME_AXIS_WIDTH,
} from './roomCalendarUtils';

import type { Dashboard } from '../../Types/AuthServer';

/** Gap left between two lanes of the same day column, and the floor a 15-minute booking keeps. */
const EVENT_LANE_GAP_PX = 6;
const MIN_EVENT_HEIGHT_PERCENTAGE = 3.8;
const MIN_EVENT_WIDTH_PX = 42;

function getEventHeightPercentage(event: MultiRoomPositionedEvent, hourRangeMinutes: number) {
    return Math.max(
        ((event.endMinutes - event.startMinutes) * 100) / hourRangeMinutes,
        MIN_EVENT_HEIGHT_PERCENTAGE,
    );
}

function getEventWidthPx(event: MultiRoomPositionedEvent, dayColumnWidthPx: number) {
    return Math.max(
        Math.round(dayColumnWidthPx / Math.max(event.laneCount, 1) - EVENT_LANE_GAP_PX),
        MIN_EVENT_WIDTH_PX,
    );
}

interface InfoscreenMultiRoomWeekCalendarViewProps {
    currentTime: dayjs.Dayjs;
    infoscreenLayout: Dashboard['socials'];
    rooms: readonly MultiRoomTarget[];
    showEventTitles: boolean;
    showWeekend: boolean;
    timezone: string;
}

/**
 * Fetches each room's events for the whole visible week in one combined request. A generated SWR hook
 * called once per room in a `.map()` would violate rules-of-hooks since room count varies across
 * renders, so this uses one `useSwr` call whose fetcher runs the raw (non-hook) API calls in parallel.
 */
function useMultiRoomWeekEvents(rooms: readonly MultiRoomTarget[], weekStart: dayjs.Dayjs) {
    const weekStartIso = weekStart.toISOString();
    const weekEndIso = weekStart.add(6, 'day').endOf('day').toISOString();
    const calendarIdsKey = rooms.map((room) => room.calendarId).join(',');

    const { data, isLoading } = useSwr(
        calendarIdsKey ? ['infoscreen-multi-room-week-events', calendarIdsKey, weekStartIso] : null,
        () =>
            Promise.all(
                rooms.map((room) =>
                    usersCalendarEventsList({ calendar: room.calendarId, end: weekEndIso, start: weekStartIso }),
                ),
            ),
        {
            refreshInterval: 60_000,
            revalidateOnFocus: false,
        },
    );

    return { eventsByRoom: data, isLoading: calendarIdsKey ? isLoading : false };
}

export default function InfoscreenMultiRoomWeekCalendarView({
    currentTime,
    infoscreenLayout,
    rooms,
    showEventTitles,
    showWeekend,
    timezone,
}: Readonly<InfoscreenMultiRoomWeekCalendarViewProps>) {
    const calendarGridRef = useRef<HTMLDivElement | null>(null);
    const [calendarGridWidthPx, setCalendarGridWidthPx] = useState(0);
    const [calendarGridHeightPx, setCalendarGridHeightPx] = useState(0);

    const weekStart = useMemo(() => getWeekStart(currentTime), [currentTime]);
    const weekDays = useMemo(() => Array.from({ length: 7 }, (_, index) => weekStart.add(index, 'day')), [weekStart]);
    const visibleDays = useMemo(
        () => (showWeekend ? weekDays : weekDays.filter((day) => !isWeekendDay(day))),
        [showWeekend, weekDays],
    );

    const { eventsByRoom, isLoading } = useMultiRoomWeekEvents(rooms, weekStart);

    const weekRangeLabel = `${weekStart.format('D.M.YYYY')} - ${weekStart.add(6, 'day').format('D.M.YYYY')}`;

    const roomEventGroups = useMemo(
        () =>
            rooms.map((room, index) => ({
                events: eventsByRoom?.[index] ?? [],
                roomColor: getRoomWeekColor(index),
                roomId: room.id,
                roomLabel: room.label,
            })),
        [eventsByRoom, rooms],
    );

    const hourRange = useMemo(
        () => getWeekHourRange({ days: visibleDays, roomEventGroups, timezone }),
        [roomEventGroups, timezone, visibleDays],
    );
    const hourRangeMinutes = getHourRangeMinutes(hourRange);
    const visibleHourCount = hourRange.endHour - hourRange.startHour;

    const dayData = useMemo(
        () =>
            visibleDays.map((day) =>
                getMultiRoomWeekDayData({
                    currentDay: day,
                    hourRange,
                    roomEventGroups,
                    showEventTitles,
                    timezone,
                }),
            ),
        [hourRange, roomEventGroups, showEventTitles, timezone, visibleDays],
    );

    const hasTimedEvents = dayData.some((day) => day.positionedEvents.length > 0);
    const hasAllDayEvents = dayData.some((day) => day.allDayEvents.length > 0);
    const bodyRow = hasAllDayEvents ? 3 : 2;
    const visibleDayCount = Math.max(visibleDays.length, 1);
    const dayColumnWidthPx = Math.max((calendarGridWidthPx - TIME_AXIS_WIDTH) / visibleDayCount, 0);
    const measuredGridBodyHeightPx =
        calendarGridHeightPx - DAY_HEADER_HEIGHT - (hasAllDayEvents ? ALL_DAY_ROW_HEIGHT : 0);
    const gridBodyHeightPx = measuredGridBodyHeightPx > 0 ? measuredGridBodyHeightPx : FALLBACK_GRID_BODY_HEIGHT_PX;
    // One title size and one time size for the whole week, computed over every box at once. Sizing
    // each box on its own made a short name in a narrow lane render larger than a long name beside
    // it, which reads as a rendering fault from across the lobby.
    const eventTypography = getUniformEventTypography(
        dayData.flatMap((day) =>
            day.positionedEvents.map((event) => ({
                eventHeightPx: (getEventHeightPercentage(event, hourRangeMinutes) / 100) * gridBodyHeightPx,
                eventWidthPx: getEventWidthPx(event, dayColumnWidthPx),
                label: event.publicLabel,
            })),
        ),
    );

    useEffect(() => {
        const gridElement = calendarGridRef.current;

        if (!gridElement) {
            return;
        }

        const measureGrid = () => {
            const nextGridWidthPx = Math.floor(gridElement.clientWidth);
            const nextGridHeightPx = Math.floor(gridElement.clientHeight);

            setCalendarGridWidthPx((currentWidth) =>
                currentWidth === nextGridWidthPx ? currentWidth : nextGridWidthPx,
            );
            setCalendarGridHeightPx((currentHeight) =>
                currentHeight === nextGridHeightPx ? currentHeight : nextGridHeightPx,
            );
        };

        measureGrid();

        let observer: ResizeObserver | null = null;

        if (typeof ResizeObserver !== 'undefined') {
            observer = new ResizeObserver(() => {
                measureGrid();
            });
            observer.observe(gridElement);
        }

        return () => {
            if (observer) {
                observer.disconnect();
            }
        };
        // `isLoading` is a dependency because the grid does not exist during the loading state: without
        // it the first mount ran this effect against a null ref, never attached the ResizeObserver, and
        // the whole week then sized its boxes from a 0px grid — every lane at the 42px floor, which is
        // why the view showed only start times until it was remounted from a warm SWR cache.
    }, [hasAllDayEvents, isLoading, rooms.length]);

    if (isLoading) {
        return (
            <Stack
                sx={{
                    alignItems: 'center',
                    color: infoscreenLayout?.textColor || '#ffffff',
                    height: '100%',
                    justifyContent: 'center',
                    width: '100%',
                }}
            >
                <Typography sx={{ fontSize: 'clamp(18px, 2vw, 30px)', fontWeight: 600 }}>
                    Loading room calendars
                </Typography>
            </Stack>
        );
    }

    return (
        <Stack
            sx={{
                backgroundColor: infoscreenLayout?.backgroundColor || '#000000',
                boxSizing: 'border-box',
                color: infoscreenLayout?.textColor || '#ffffff',
                gap: 0.75,
                height: '100%',
                px: { sm: 1.5, xs: 1 },
                py: { sm: 1.25, xs: 1 },
                width: '100%',
            }}
        >
            <Stack direction="row" sx={{ alignItems: 'flex-start', gap: 2, justifyContent: 'space-between', pr: '104px' }}>
                <Box>
                    <Typography noWrap sx={{ fontSize: 'clamp(18px, 1.8vw, 28px)', fontWeight: 700 }}>
                        {rooms.map((room) => room.label).join(', ')}
                    </Typography>
                    <Typography sx={{ fontSize: 'clamp(11px, 0.9vw, 15px)', opacity: 0.75 }}>
                        Current week · {weekRangeLabel}
                    </Typography>
                    <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                        {rooms.map((room, index) => (
                            <Stack direction="row" key={room.id} sx={{ alignItems: 'center', gap: 0.5 }}>
                                <Box
                                    sx={{
                                        backgroundColor: getRoomWeekColor(index),
                                        borderRadius: '50%',
                                        height: 9,
                                        width: 9,
                                    }}
                                />
                                <Typography noWrap sx={{ fontSize: 'clamp(10px, 0.8vw, 13px)', opacity: 0.85 }}>
                                    {room.label}
                                </Typography>
                            </Stack>
                        ))}
                    </Stack>
                </Box>
                <Typography sx={{ fontSize: 'clamp(13px, 1vw, 17px)', opacity: 0.9, pt: 0.35, textAlign: 'right' }}>
                    {currentTime.format('ddd D.M. HH:mm')}
                </Typography>
            </Stack>

            <Box
                ref={calendarGridRef}
                sx={{
                    border: '1px solid rgba(255,255,255,0.14)',
                    borderRadius: 1.5,
                    display: 'grid',
                    flex: 1,
                    gridTemplateColumns: `${TIME_AXIS_WIDTH.toString()}px repeat(${visibleDayCount.toString()}, minmax(0, 1fr))`,
                    gridTemplateRows: hasAllDayEvents
                        ? `${DAY_HEADER_HEIGHT.toString()}px ${ALL_DAY_ROW_HEIGHT.toString()}px minmax(0, 1fr)`
                        : `${DAY_HEADER_HEIGHT.toString()}px minmax(0, 1fr)`,
                    minHeight: 0,
                    overflow: 'hidden',
                }}
            >
                <Box
                    sx={{
                        backgroundColor: 'rgba(255,255,255,0.04)',
                        borderBottom: '1px solid rgba(255,255,255,0.12)',
                        borderRight: '1px solid rgba(255,255,255,0.12)',
                        gridColumn: '1 / 2',
                        gridRow: '1 / 2',
                    }}
                />

                {visibleDays.map((day, index) => (
                    <Stack
                        key={`header-${day.format('YYYY-MM-DD')}`}
                        sx={{
                            backgroundColor: day.isSame(currentTime, 'day')
                                ? 'rgba(59,130,246,0.14)'
                                : 'rgba(255,255,255,0.03)',
                            borderBottom: '1px solid rgba(255,255,255,0.12)',
                            borderRight: index === visibleDays.length - 1 ? 'none' : '1px solid rgba(255,255,255,0.08)',
                            gridColumn: `${(index + 2).toString()} / ${(index + 3).toString()}`,
                            gridRow: '1 / 2',
                            justifyContent: 'center',
                            px: 0.75,
                        }}
                    >
                        <Typography noWrap sx={{ fontSize: 'clamp(11px, 0.82vw, 13px)', fontWeight: 700 }}>
                            {`${day.format('ddd').toUpperCase()} ${day.format('D.M.')}`}
                        </Typography>
                    </Stack>
                ))}

                {hasAllDayEvents ? (
                    <>
                        <Box
                            sx={{
                                alignItems: 'center',
                                backgroundColor: 'rgba(255,255,255,0.04)',
                                borderBottom: '1px solid rgba(255,255,255,0.12)',
                                borderRight: '1px solid rgba(255,255,255,0.12)',
                                display: 'flex',
                                gridColumn: '1 / 2',
                                gridRow: '2 / 3',
                                justifyContent: 'center',
                            }}
                        >
                            <Typography sx={{ fontSize: '11px', letterSpacing: '0.08em', opacity: 0.7 }}>
                                ALL DAY
                            </Typography>
                        </Box>
                        {visibleDays.map((day, index) => (
                            <Stack
                                key={`all-day-${day.format('YYYY-MM-DD')}`}
                                sx={{
                                    backgroundColor: day.isSame(currentTime, 'day')
                                        ? 'rgba(59,130,246,0.16)'
                                        : 'rgba(255,255,255,0.03)',
                                    borderBottom: '1px solid rgba(255,255,255,0.12)',
                                    borderRight:
                                        index === visibleDays.length - 1 ? 'none' : '1px solid rgba(255,255,255,0.08)',
                                    gap: 0.35,
                                    gridColumn: `${(index + 2).toString()} / ${(index + 3).toString()}`,
                                    gridRow: '2 / 3',
                                    px: 0.5,
                                    py: 0.4,
                                }}
                            >
                                {dayData[index]?.allDayEvents.length ? (
                                    dayData[index].allDayEvents.slice(0, 2).map((event) => (
                                        <Box
                                            key={`${day.format('YYYY-MM-DD')}-${event.roomLabel}-${event.label}`}
                                            sx={{
                                                backgroundColor: event.roomColor,
                                                borderRadius: 999,
                                                overflow: 'hidden',
                                                px: 0.8,
                                                py: 0.3,
                                            }}
                                        >
                                            <Typography noWrap sx={{ fontSize: '11px', fontWeight: 600 }}>
                                                {event.label}
                                            </Typography>
                                        </Box>
                                    ))
                                ) : (
                                    <Typography sx={{ fontSize: '11px', opacity: 0.45, pt: 0.3, px: 0.4 }}>
                                        No all-day bookings
                                    </Typography>
                                )}
                            </Stack>
                        ))}
                    </>
                ) : null}

                <Box
                    sx={{
                        backgroundColor: 'rgba(255,255,255,0.03)',
                        borderRight: '1px solid rgba(255,255,255,0.12)',
                        gridColumn: '1 / 2',
                        gridRow: `${bodyRow.toString()} / ${(bodyRow + 1).toString()}`,
                        position: 'relative',
                    }}
                >
                    {Array.from(
                        { length: visibleHourCount + 1 },
                        (_, index) => hourRange.startHour + index,
                    ).map(
                        (hour) => {
                            const topPercentage = getHourLinePercentage(hour, hourRange);
                            const isFirstHour = hour === hourRange.startHour;
                            const isLastHour = hour === hourRange.endHour;

                            return (
                                <Typography
                                    key={`hour-${hour.toString()}`}
                                    sx={{
                                        bottom: isLastHour ? 0 : 'auto',
                                        fontSize: '11px',
                                        letterSpacing: '0.04em',
                                        lineHeight: 1,
                                        opacity: 0.8,
                                        position: 'absolute',
                                        right: 8,
                                        top: isLastHour ? 'auto' : `${topPercentage.toString()}%`,
                                        transform: isFirstHour || isLastHour ? 'none' : 'translateY(-50%)',
                                        zIndex: 2,
                                    }}
                                >
                                    {getVisibleTimeLabel(hour)}
                                </Typography>
                            );
                        },
                    )}
                </Box>

                {visibleDays.map((day, index) => {
                    const nowLineVisible = day.isSame(currentTime, 'day') && currentTime.hour() >= hourRange.startHour;
                    const currentMinutes = currentTime.diff(
                        day.hour(hourRange.startHour).minute(0).second(0).millisecond(0),
                        'minute',
                    );
                    const currentLineTop = clampMinutes(currentMinutes, hourRange);

                    return (
                        <Box
                            key={day.format('YYYY-MM-DD')}
                            sx={{
                                backgroundColor: day.isSame(currentTime, 'day')
                                    ? 'rgba(59,130,246,0.10)'
                                    : 'rgba(255,255,255,0.015)',
                                backgroundImage: `repeating-linear-gradient(to bottom, transparent, transparent calc((100% / ${visibleHourCount.toString()}) - 1px), rgba(255,255,255,0.08) calc((100% / ${visibleHourCount.toString()}) - 1px), rgba(255,255,255,0.08) calc(100% / ${visibleHourCount.toString()}))`,
                                borderRight:
                                    index === visibleDays.length - 1 ? 'none' : '1px solid rgba(255,255,255,0.08)',
                                gridColumn: `${(index + 2).toString()} / ${(index + 3).toString()}`,
                                gridRow: `${bodyRow.toString()} / ${(bodyRow + 1).toString()}`,
                                position: 'relative',
                            }}
                        >
                            <Box
                                sx={{
                                    bottom: 0,
                                    left: 0,
                                    position: 'absolute',
                                    right: 0,
                                    top: 0,
                                }}
                            >
                                {dayData[index]?.positionedEvents.map((event) => {
                                    const top = (event.startMinutes * 100) / hourRangeMinutes;
                                    const height = getEventHeightPercentage(event, hourRangeMinutes);
                                    const width = `calc(${(100 / event.laneCount).toString()}% - ${EVENT_LANE_GAP_PX.toString()}px)`;
                                    const left = `calc(${((100 / event.laneCount) * event.lane).toString()}% + ${(EVENT_LANE_GAP_PX / 2).toString()}px)`;
                                    const timedEventLayout = getTimedEventLayout({
                                        eventHeightPx: (height / 100) * gridBodyHeightPx,
                                        eventWidthPx: getEventWidthPx(event, dayColumnWidthPx),
                                        label: event.publicLabel,
                                        typography: eventTypography,
                                    });
                                    const eventStart = day
                                        .hour(hourRange.startHour)
                                        .minute(0)
                                        .second(0)
                                        .millisecond(0)
                                        .add(event.startMinutes, 'minute');
                                    const eventEnd = day
                                        .hour(hourRange.startHour)
                                        .minute(0)
                                        .second(0)
                                        .millisecond(0)
                                        .add(event.endMinutes, 'minute');
                                    const timeLabel = formatCompactEventTimeRange(eventStart, eventEnd);

                                    return (
                                        <Box
                                            key={event.id}
                                            sx={{
                                                backgroundColor: event.roomColor,
                                                border: '1px solid rgba(0,0,0,0.18)',
                                                borderRadius: 1,
                                                boxShadow: '0 4px 8px rgba(0,0,0,0.18)',
                                                height: `calc(${height.toString()}% - ${(timedEventLayout.verticalInsetPx * 2).toString()}px)`,
                                                left,
                                                // Grow to exactly what the text paints, never to an
                                                // estimated worst case, so a short booking stays as
                                                // close to its own slot height as its content allows.
                                                minHeight: 'fit-content',
                                                overflow: 'hidden',
                                                position: 'absolute',
                                                top: `calc(${top.toString()}% + ${timedEventLayout.verticalInsetPx.toString()}px)`,
                                                width,
                                                zIndex: 2,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    boxSizing: 'border-box',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: `${timedEventLayout.gapPx.toString()}px`,
                                                    minWidth: 0,
                                                    px: 0.25,
                                                    py: 0.25,
                                                    width: '100%',
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        display: '-webkit-box',
                                                        fontSize: `${timedEventLayout.labelFontSizePx.toString()}px !important`,
                                                        fontWeight: 700,
                                                        lineHeight: EVENT_LINE_HEIGHT,
                                                        minWidth: 0,
                                                        overflow: 'hidden',
                                                        overflowWrap: 'anywhere',
                                                        WebkitBoxOrient: 'vertical',
                                                        WebkitLineClamp: timedEventLayout.labelLineClamp,
                                                    }}
                                                >
                                                    {event.publicLabel}
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        display: '-webkit-box',
                                                        flexShrink: 0,
                                                        fontSize: `${timedEventLayout.timeFontSizePx.toString()}px !important`,
                                                        fontWeight: 600,
                                                        lineHeight: EVENT_LINE_HEIGHT,
                                                        opacity: 0.9,
                                                        // Narrow lanes break the range after the hyphen instead of
                                                        // ellipsing the end time away.
                                                        overflow: 'hidden',
                                                        overflowWrap: 'anywhere',
                                                        WebkitBoxOrient: 'vertical',
                                                        WebkitLineClamp: timedEventLayout.timeLineCount,
                                                    }}
                                                >
                                                    {timeLabel}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    );
                                })}

                                {nowLineVisible && currentLineTop <= hourRangeMinutes ? (
                                    <Box
                                        sx={{
                                            borderTop: '1px solid #ff6b6b',
                                            boxShadow: '0 0 3px rgba(0, 0, 0, 0.7)',
                                            left: 0,
                                            opacity: 0.9,
                                            position: 'absolute',
                                            right: 0,
                                            top: `${((currentLineTop * 100) / hourRangeMinutes).toString()}%`,
                                            zIndex: 4,
                                        }}
                                    />
                                ) : null}
                            </Box>
                        </Box>
                    );
                })}
            </Box>

            {!hasTimedEvents && !hasAllDayEvents ? (
                <Typography sx={{ fontSize: 'clamp(12px, 1vw, 16px)', opacity: 0.7, textAlign: 'center' }}>
                    No room reservations were found for this week.
                </Typography>
            ) : null}
        </Stack>
    );
}
